import { useImmer } from "use-immer";
import { useEffect, useState } from "react";
import ApiConnector from "../connector/ApiConnector";
import moment from "moment";
import { Link } from "react-router-dom";
import CalculateWeightsJobParameterSimulationPage from "../component/simulationPage/CalculateWeightsJobParameterSimulationPage";
import frontendConstant from "../constant/FrontendConstant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import { useTableModel } from "../service/TableModelUtilService";
import { normalizeRowsForSignature } from "../service/NormalizeForSignatureUtilService";

const Component = (props) => {
  const calculateWeightsJobParameterTemplate = {
    "@type": "CalculateWeightsJobParameter",
    trnDateMin: moment().subtract(30, "days").format("YYYY-MM-DDT00:00:00") + "Z",
    trnDateMax: moment().format("YYYY-MM-DDT00:00:00") + "Z",
    falsePositivesMax: 85,
    truePositivesMin: 80,
    runDurationSecondsMax: frontendConstant.lambdaRunDurationSecondsMax,
    threshold: 4.0,
    foundWeightsMax: 10,
    ignoredRuleScoreColumns: ["auto_allow_rule_score", "card_lock_auto_allow_rule_score"],
  };
  const [jobs, updateJobs] = useImmer([]);
  const [jobDetails, updateJobDetails] = useImmer([]);
  const [newJob, _updateNewJob] = useImmer(structuredClone(calculateWeightsJobParameterTemplate));
  const [ruleInfoItems, updateRuleInfoItems] = useImmer([]);
  const [mandatoryIgnoredRuleScoreColumns, updateMandatoryIgnoredRuleScoreColumns] = useImmer([]);
  const [weightRecalculationInfoOpen, setWeightRecalculationInfoOpen] = useState(false);

  const handleInfoIconClick = (detailsId, isOpen, setOpen) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!isOpen);
  };

  const { canSave, setRows: setModelRows, setBaselineFromRows } = useTableModel({
    initialRows: [],
    columns: [],
    keyField: "_key",
    initialSortBy: null,
    initialSortDir: "asc",
    normalizeForSignature: normalizeRowsForSignature,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      await loadAndInit();
      if (!mounted) return;
    })();
    return () => { mounted = false; };
  }, []);

  const loadAndInit = async () => {
    const _jobs = await ApiConnector.getJobHeaders(["RUN_WEIGHTS_RECALCULATION_JOB"]);
    updateJobs(_jobs);

    if (_jobs.length > 0) {
      const jobIds = _jobs.map((j) => j.id);
      const _jobDetails = await ApiConnector.getCalculateWeightsJobResultDetails(jobIds);
      updateJobDetails(_jobDetails);

      const params = _jobs[0].parameter; // Set new job parameters to recent job parameters to safe User an effort
      _updateNewJob(params);
      updateMandatoryIgnoredRuleScoreColumns(
        await findMandatoryIgnoredColumns(params.trnDateMin, params.trnDateMax)
      );
      setModelRows([params]);
      setBaselineFromRows([params]);
    } else {
      updateMandatoryIgnoredRuleScoreColumns(
        await findMandatoryIgnoredColumns(newJob.trnDateMin, newJob.trnDateMax)
      );
      setModelRows([newJob]);
      setBaselineFromRows([newJob]);
    }
  };

  const updateNewJob = async (e) => {
    const isTrnDateChanged = newJob.trnDateMin + newJob.trnDateMax !== e.trnDateMin + e.trnDateMax;
    if (isTrnDateChanged) {
      updateMandatoryIgnoredRuleScoreColumns(
        await findMandatoryIgnoredColumns(e.trnDateMin, e.trnDateMax)
      );
    }
    _updateNewJob(e);
    setModelRows([e]);
  };

  const findMandatoryIgnoredColumns = async (trnDateMin, trnDateMax) => {
    const ruleInfo = await ApiConnector.getRuleInfo(trnDateMin, trnDateMax);
    updateRuleInfoItems(ruleInfo.ruleInfoItems);
    return ruleInfo.ruleInfoItems
      .filter((i) => i.notEvaluatedDates.length > 0)
      .map((i) => i.ruleScoreColumnName);
  };

  const findJobDetail = (jobId) => {
    const _jobDetail = jobDetails.filter((jd) => jd.jobId === jobId);
    return _jobDetail ? _jobDetail[0] : null;
  };

  const saveJob = async () => {
    if (!canSave) return;

    const isMaxInitJobs = jobs.filter((j) => ["INIT"].includes(j.status)).length >= 4;
    if (isMaxInitJobs) {
      window.alert("There are already jobs in progress. Wait until some of them will finish.");
      return;
    }
    const job = {
      type: "RUN_WEIGHTS_RECALCULATION_JOB",
      parameter: {
        ...newJob,
        trnDateMin: newJob.trnDateMin,
        trnDateMax: newJob.trnDateMax,
        ignoredRuleScoreColumns: [
          ...new Set([...mandatoryIgnoredRuleScoreColumns, ...newJob.ignoredRuleScoreColumns]),
        ],
      },
    };
    await ApiConnector.postJob(job);
    await loadAndInit();
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1 className="header-title">Simulation</h1>
        <span className="header-save">
          <FontAwesomeIcon
            icon={faFloppyDisk}
            size="2xl"
            className="icon-clickable"
            onClick={canSave ? saveJob : undefined}
            title={canSave ? "Save changes" : "No changes to save"}
            style={{
              opacity: canSave ? 1 : 0.4,
              cursor: canSave ? "pointer" : "not-allowed",
              pointerEvents: canSave ? "auto" : "none",
            }}
            aria-disabled={!canSave}
          />
        </span>
      </header>
      <main>
        <div className="row">
          <div className="col">
            <h2>Weight recalculation job</h2>
          </div>
          <div className="col-4">
            <h2 className="float-right">
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('weightRecalculation-details', weightRecalculationInfoOpen, setWeightRecalculationInfoOpen)}
                title={weightRecalculationInfoOpen ? "Hide info" : "Show info"}
              />
            </h2>
          </div>
        </div>

        {weightRecalculationInfoOpen && (
          <div className="info-box">
            <p>
              <strong>Manages the automatic weight recalculation process</strong> via a dedicated UI page.
              It includes manual job triggers, backend business logic, a specialized computation lambda,
              and configuration settings to ignore specific rules, if necessary.
            </p>
          </div>
        )}

        <CalculateWeightsJobParameterSimulationPage
          jobParam={newJob}
          ruleInfoItems={ruleInfoItems}
          mandatoryIgnoredRuleScoreColumns={mandatoryIgnoredRuleScoreColumns}
          onChange={async (e) => updateNewJob(e)}
        ></CalculateWeightsJobParameterSimulationPage>

        <h2>Jobs</h2>
        <>
          <table className="config-table-double">
            <thead>
            <tr>
              <th>Created</th>
              <th>User id</th>
              <th>Status</th>
              <th>
                Best False
                <br />
                positives
              </th>
              <th>
                Best True
                <br />
                positives
              </th>
              <th>Result</th>
            </tr>
            </thead>
            <tbody>
            {jobs &&
              jobs.map((job, idx) => (
                <tr key={idx}>
                  <td>{moment(job.created).format("YYYY-MM-DD HH:mm:ss")}</td>
                  <td>{job.userId}</td>
                  <td>{job.status}</td>
                  <td>
                    {findJobDetail(job.id) ? findJobDetail(job.id).bestWeightResult.falsePositives : ""}
                  </td>
                  <td>
                    {findJobDetail(job.id) ? findJobDetail(job.id).bestWeightResult.truePositives : ""}
                  </td>
                  <td>
                    <Link to={"/simulation/result?jobId=" + job.id}>View detail</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      </main>
    </>
  );
};

export default Component;
